import { useState, useRef, useEffect } from 'react';

interface SignatureCaptureProps {
  onCapture: (data: SignatureData) => void;
  onCancel: () => void;
}

interface SignatureData {
  type: 'drawn' | 'typed' | 'uploaded' | 'camera';
  data: string; // base64 for images, text for typed
}

export default function SignatureCapture({ onCapture, onCancel }: SignatureCaptureProps) {
  const [mode, setMode] = useState<'draw' | 'type' | 'upload' | 'camera'>('draw');
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup camera stream when component unmounts
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (mode === 'draw') {
      initCanvas();
    }
  }, [mode]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      e.preventDefault();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
  };

  const handleCapture = () => {
    if (mode === 'draw') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const data = canvas.toDataURL('image/png');
      onCapture({ type: 'drawn', data });
    } else if (mode === 'type') {
      if (!typedName.trim()) return;
      onCapture({ type: 'typed', data: typedName });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      onCapture({ type: 'uploaded', data: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Failed to access camera:', err);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(videoRef.current, 0, 0);
    const data = canvas.toDataURL('image/png');

    // Stop camera
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }

    onCapture({ type: 'camera', data });
  };

  return (
    <div className="signature-capture">
      <div className="signature-tabs">
        <button
          className={`sig-tab ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => setMode('draw')}
        >
          Draw
        </button>
        <button
          className={`sig-tab ${mode === 'type' ? 'active' : ''}`}
          onClick={() => setMode('type')}
        >
          Type
        </button>
        <button
          className={`sig-tab ${mode === 'upload' ? 'active' : ''}`}
          onClick={() => setMode('upload')}
        >
          Upload
        </button>
        <button
          className={`sig-tab ${mode === 'camera' ? 'active' : ''}`}
          onClick={() => {
            setMode('camera');
            startCamera();
          }}
        >
          Camera
        </button>
      </div>

      <div className="signature-content">
        {mode === 'draw' && (
          <div className="draw-area">
            <canvas
              ref={canvasRef}
              width={500}
              height={200}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{ touchAction: 'none' }}
            />
            <button onClick={clearCanvas} className="btn btn-small btn-secondary">
              Clear
            </button>
          </div>
        )}

        {mode === 'type' && (
          <div className="type-area">
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full legal name"
              className="signature-input"
            />
            <div className="signature-preview" style={{ fontFamily: 'cursive', fontSize: '2rem' }}>
              {typedName || 'Your signature will appear here'}
            </div>
          </div>
        )}

        {mode === 'upload' && (
          <div className="upload-area">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              id="signature-upload"
            />
            <label htmlFor="signature-upload" className="upload-label">
              Click to upload or drag and drop your signature image
            </label>
          </div>
        )}

        {mode === 'camera' && (
          <div className="camera-area">
            <video ref={videoRef} style={{ maxWidth: '100%', borderRadius: '8px' }} />
            {cameraStream && (
              <button onClick={captureFromCamera} className="btn btn-primary">
                Capture Photo
              </button>
            )}
          </div>
        )}
      </div>

      <div className="signature-actions">
        <button onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        {(mode === 'draw' || mode === 'type') && (
          <button
            onClick={handleCapture}
            className="btn btn-primary"
            disabled={mode === 'type' && !typedName.trim()}
          >
            Confirm Signature
          </button>
        )}
      </div>

      <style>{`
        .signature-capture {
          background: white;
          border-radius: 8px;
          padding: 24px;
        }

        .signature-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 12px;
        }

        .sig-tab {
          padding: 8px 16px;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .sig-tab:hover {
          background: #f3f4f6;
        }

        .sig-tab.active {
          background: #4f46e5;
          color: white;
        }

        .signature-content {
          min-height: 220px;
          margin-bottom: 24px;
        }

        .draw-area canvas {
          border: 2px dashed #e5e7eb;
          border-radius: 8px;
          cursor: crosshair;
          display: block;
          margin-bottom: 12px;
        }

        .type-area {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .signature-input {
          padding: 12px 16px;
          font-size: 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .signature-preview {
          padding: 24px;
          border: 2px dashed #e5e7eb;
          border-radius: 8px;
          text-align: center;
          color: #374151;
          min-height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .upload-area {
          border: 2px dashed #e5e7eb;
          border-radius: 8px;
          padding: 48px;
          text-align: center;
        }

        .upload-area input {
          display: none;
        }

        .upload-label {
          cursor: pointer;
          color: #6b7280;
        }

        .camera-area {
          display: flex;
          flex-direction: column;
          gap: 16px;
          align-items: center;
        }

        .signature-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
      `}</style>
    </div>
  );
}
